import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { KBAdmin } from '../components/admin/KBAdmin';
import { ManualsAdmin } from '../components/admin/ManualsAdmin';
import { CourseEditExtras } from '../components/admin/CourseEditExtras';
import { LeadsMarketing } from '../components/admin/LeadsMarketing';
import { QuoteRequestsAdmin } from '../components/admin/QuoteRequestsAdmin';
import { MessagesInboxAdmin } from '../components/admin/MessagesInboxAdmin';
import { EmailHistoryAdmin } from '../components/admin/EmailHistoryAdmin';
import { AnnouncementAdmin } from '../components/admin/AnnouncementAdmin';
import { SmtpSettingsAdmin } from '../components/admin/SmtpSettingsAdmin';
import { SeoTrackingAdmin } from '../components/admin/SeoTrackingAdmin';
import { UserManagementAdmin } from '../components/admin/UserManagementAdmin';
import { DossierPipelineAdmin } from '../components/admin/DossierPipelineAdmin';
import { TrainersAdmin } from '../components/admin/TrainersAdmin';
import { DashboardShell } from '../components/dashboard/DashboardShell';
import { AdminNotificationsBell } from '../components/admin/AdminNotificationsBell';
import { UserRecapSheet } from '../components/admin/UserRecapSheet';
import { 
  Users, 
  BookOpen, 
  Euro, 
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  Settings,
  BarChart3,
  Calendar,
  Search,
  HelpCircle,
  BookMarked,
  FolderOpen,
  UserCheck,
  TrendingUp,
  Globe,
  Check,
  GraduationCap,
  Mail
} from 'lucide-react';

const categoryLabels = {
  decouverte: 'Découverte',
  complete: 'Formation Complète',
  auto: 'Sellerie Auto',
  moto: 'Sellerie Moto',
  nautique: 'Sellerie Nautique',
  habitat: 'Habitat & Médical',
  dessin: 'Dessin Technique'
};

const insightLabels = {
  discovery: {
    google: 'Moteur de recherche',
    social: 'Réseaux sociaux',
    word_of_mouth: 'Bouche-à-oreille',
    press: 'Presse / Article',
    event: 'Salon / Événement',
    other: 'Autre source',
  },
  social: {
    instagram: 'Instagram',
    facebook: 'Facebook',
    tiktok: 'TikTok',
    youtube: 'YouTube',
    linkedin: 'LinkedIn',
    pinterest: 'Pinterest / Autre',
  },
  situation: {
    employed: 'En poste',
    retraining: 'En reconversion',
    jobseeker: 'Demandeur d\'emploi',
    entrepreneur: 'Entrepreneur',
    future_entrepreneur: 'Futur entrepreneur',
    student: 'Étudiant·e',
  },
  goal: {
    main_activity: 'Métier principal',
    secondary: 'Activité complémentaire',
    passion: 'Passion personnelle',
    exploring: 'En exploration',
  },
};

const InsightBar = ({ label, count, percentage }) => (
  <div className="space-y-1.5">
    <div className="flex items-center justify-between text-sm">
      <span className="text-foreground font-medium">{label}</span>
      <span className="text-muted-foreground">
        <strong className="text-[#C9B896]">{count}</strong> · {percentage}%
      </span>
    </div>
    <div className="h-2 rounded-full bg-navy-light/30 overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-beige to-beige-light transition-all duration-700"
        style={{ width: `${Math.max(percentage, 2)}%` }}
      />
    </div>
  </div>
);

const InsightCard = ({ title, subtitle, items, labelMap }) => (
  <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6">
    <h3 className="font-heading text-lg font-semibold mb-1">{title}</h3>
    {subtitle && <p className="text-xs text-muted-foreground mb-5">{subtitle}</p>}
    {(!items || items.length === 0) ? (
      <p className="text-sm text-muted-foreground italic py-4">Aucune donnée pour le moment.</p>
    ) : (
      <div className="space-y-3.5">
        {items.map((item) => (
          <InsightBar
            key={item.key}
            label={labelMap?.[item.key] || item.key}
            count={item.count}
            percentage={item.percentage}
          />
        ))}
      </div>
    )}
  </div>
);

export const AdminDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [insights, setInsights] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  // Initial section can be set from the ?section=… URL param (used by
  // notification bell deep-links). Falls back to "overview".
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === 'undefined') return 'overview';
    const params = new URLSearchParams(window.location.search);
    return params.get('section') || 'overview';
  });

  // React to URL changes (back/forward) and deep-link clicks while the page
  // stays mounted (the notification bell uses navigate() that updates the
  // URL but does not remount this component).
  useEffect(() => {
    const onLocation = () => {
      const params = new URLSearchParams(window.location.search);
      const section = params.get('section');
      if (section && section !== activeSection) setActiveSection(section);
    };
    window.addEventListener('popstate', onLocation);
    // Manual polling fallback — fires once per setActiveSection from notif click
    const id = setInterval(onLocation, 800);
    return () => {
      window.removeEventListener('popstate', onLocation);
      clearInterval(id);
    };
  }, [activeSection]);

  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    category: 'decouverte',
    course_type: 'online',
    price: '',
    duration_days: '',
    max_participants: '',
    location: 'Istres',
    thumbnail_url: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsData, usersData, coursesData, insightsData] = await Promise.all([
        api.getStats(),
        api.getAllUsers(),
        api.getCourses(),
        api.getApplicationsInsights().catch(() => null)
      ]);
      setStats(statsData);
      setUsers(usersData);
      setCourses(coursesData);
      setInsights(insightsData);
      // Refresh selectedCourse with latest data (e.g. programme_pdf_path) if open.
      setSelectedCourse((prev) => prev ? (coursesData.find((c) => c.id === prev.id) || prev) : prev);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...courseForm,
        price: parseFloat(courseForm.price),
        duration_days: courseForm.duration_days ? parseInt(courseForm.duration_days) : null,
        max_participants: courseForm.max_participants ? parseInt(courseForm.max_participants) : null
      };
      
      if (selectedCourse) {
        await api.updateCourse(selectedCourse.id, data);
        toast.success('Formation mise à jour');
      } else {
        await api.createCourse(data);
        toast.success('Formation créée');
      }
      
      setShowCourseModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handlePublish = async (courseId) => {
    try {
      await api.publishCourse(courseId);
      toast.success('Formation publiée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Supprimer cette formation ?')) return;
    try {
      await api.deleteCourse(courseId);
      toast.success('Formation supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await api.updateUserRole(userId, newRole);
      toast.success('Rôle mis à jour');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const resetForm = () => {
    setCourseForm({
      title: '',
      description: '',
      category: 'decouverte',
      course_type: 'online',
      price: '',
      duration_days: '',
      max_participants: '',
      location: 'Istres',
      thumbnail_url: ''
    });
    setSelectedCourse(null);
  };

  const editCourse = (course) => {
    setCourseForm({
      title: course.title,
      description: course.description,
      category: course.category,
      course_type: course.course_type,
      price: course.price.toString(),
      duration_days: course.duration_days?.toString() || '',
      max_participants: course.max_participants?.toString() || '',
      location: course.location || 'Istres',
      thumbnail_url: course.thumbnail_url || ''
    });
    setSelectedCourse(course);
    setShowCourseModal(true);
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.last_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Sidebar sections — drives Tabs via controlled value.
  const sections = [
    { key: 'overview', label: 'Vue d\'ensemble', Icon: BarChart3, content: null },
    { key: 'dossiers', label: 'Candidatures', Icon: FileText, content: null },
    { key: 'trainers', label: 'Formateurs', Icon: GraduationCap, badge: users.filter((u) => u.role === 'formateur').length, content: null },
    { key: 'users', label: 'Élèves', Icon: Users, badge: users.filter((u) => u.role === 'eleve').length, content: null },
    { key: 'courses', label: 'Formations', Icon: BookOpen, badge: courses.length, content: null },
    { key: 'insights', label: 'Analyse de données', Icon: TrendingUp, content: null },
    { key: 'faq', label: 'FAQ', Icon: HelpCircle, content: null },
    { key: 'kb', label: 'Base de connaissance', Icon: BookMarked, content: null },
    { key: 'manuals', label: 'Manuels & QCM', Icon: FolderOpen, content: null },
    { key: 'course-content', label: 'Cours en ligne', Icon: BookMarked, externalPath: '/admin/cours-en-ligne' },
    { key: 'leads', label: 'Leads marketing', Icon: UserCheck, content: null },
    { key: 'quotes', label: 'Devis sellerie', Icon: FileText, content: null },
    { key: 'messages', label: 'Messages reçus', Icon: Mail, content: null },
    { key: 'emails', label: 'Emails envoyés', Icon: Mail, content: null },
    { key: 'announcement', label: 'Bandeau annonce', Icon: Globe, content: null },
    { key: 'seo', label: 'SEO & Blog', Icon: Globe, content: null },
    { key: 'smtp', label: 'Paramètres SMTP', Icon: Settings, content: null },
  ];

  const CreateCourseButton = (
    <Button
      onClick={() => { resetForm(); setSelectedCourse(null); setShowCourseModal(true); }}
      className="bg-[#C9B896] text-black hover:bg-[#E0D4BA] w-full justify-center rounded-full"
      data-testid="create-course-btn"
    >
      <Plus className="w-4 h-4 mr-2" />
      Nouvelle formation
    </Button>
  );

  // Course create/edit dialog — global modal, opened from the sidebar button.
  const CourseFormFields = (
    <form onSubmit={handleCreateCourse} className="space-y-4 mt-4">
      <div className="space-y-2">
        <Label>Titre</Label>
        <Input value={courseForm.title} onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })} required data-testid="course-title-input" />
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={courseForm.description} onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })} className="min-h-[100px]" required data-testid="course-description-input" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Catégorie</Label>
          <Select value={courseForm.category} onValueChange={(val) => setCourseForm({ ...courseForm, category: val })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              {Object.entries(categoryLabels).map(([key, label]) => (<SelectItem key={key} value={key}>{label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Type</Label>
          <Select value={courseForm.course_type} onValueChange={(val) => setCourseForm({ ...courseForm, course_type: val })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="online">En ligne</SelectItem>
              <SelectItem value="onsite">Sur place</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Prix (€)</Label>
          <Input type="number" step="0.01" value={courseForm.price} onChange={(e) => setCourseForm({ ...courseForm, price: e.target.value })} required data-testid="course-price-input" />
        </div>
        <div className="space-y-2">
          <Label>Durée (jours)</Label>
          <Input type="number" value={courseForm.duration_days} onChange={(e) => setCourseForm({ ...courseForm, duration_days: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>URL de l'image</Label>
        <Input value={courseForm.thumbnail_url} onChange={(e) => setCourseForm({ ...courseForm, thumbnail_url: e.target.value })} placeholder="https://..." />
      </div>

      {selectedCourse && <CourseEditExtras course={selectedCourse} onCourseChanged={loadData} />}

      <div className="flex gap-3 pt-4 border-t">
        <Button type="submit" className="bg-[#C9B896] text-black hover:bg-[#E0D4BA]" data-testid="save-course-btn">
          {selectedCourse ? 'Mettre à jour' : 'Créer'}
        </Button>
        <Button type="button" variant="outline" onClick={() => setShowCourseModal(false)}>Annuler</Button>
      </div>
    </form>
  );

  // Overview content (KPI cards) — shown for the "overview" section.
  const OverviewContent = (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">VUE D'ENSEMBLE</p>
        <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
          La santé de <span className="font-serif italic text-[#C9B896]">l'Académie.</span>
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6" data-testid="stat-users">
          <Users className="w-4 h-4 text-[#C9B896] mb-3" />
          <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{stats?.total_users || 0}</p>
          <p className="text-sm font-medium text-[#1F2A4A] mt-2">Utilisateurs</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">tous rôles</p>
        </div>
        <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6" data-testid="stat-courses">
          <BookOpen className="w-4 h-4 text-[#C9B896] mb-3" />
          <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{stats?.total_courses || 0}</p>
          <p className="text-sm font-medium text-[#1F2A4A] mt-2">Formations</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">en catalogue</p>
        </div>
        <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6" data-testid="stat-enrollments">
          <BarChart3 className="w-4 h-4 text-[#C9B896] mb-3" />
          <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{stats?.total_enrollments || 0}</p>
          <p className="text-sm font-medium text-[#1F2A4A] mt-2">Inscriptions</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">élèves inscrits</p>
        </div>
        <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-6" data-testid="stat-revenue">
          <Euro className="w-4 h-4 text-[#C9B896] mb-3" />
          <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{stats?.total_revenue?.toFixed(0) || '0'} €</p>
          <p className="text-sm font-medium text-[#1F2A4A] mt-2">Revenus</p>
          <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mt-0.5">cumulés</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-beige border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <DashboardShell
      kicker="ADMINISTRATION"
      roleLabel="Admin"
      sections={sections}
      activeKey={activeSection}
      onSectionChange={setActiveSection}
      topSlot={CreateCourseButton}
      headerSlot={<AdminNotificationsBell variant="dark" />}
      testIdRoot="admin-dashboard"
    >
      {/* Hidden, controlled Tabs — only the active TabsContent renders. */}
      <div data-testid="admin-controlled-tabs">
        {activeSection === 'overview' && OverviewContent}
        <Tabs value={activeSection} onValueChange={setActiveSection} className="space-y-6">
          <TabsList className="hidden">
            <TabsTrigger value="overview">overview</TabsTrigger>
            <TabsTrigger value="courses">Formations</TabsTrigger>
            <TabsTrigger value="users">Élèves</TabsTrigger>
            <TabsTrigger value="trainers">Formateurs</TabsTrigger>
            <TabsTrigger value="dossiers">Candidatures</TabsTrigger>
            <TabsTrigger value="insights">Analyse de données</TabsTrigger>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="kb">Base de connaissance</TabsTrigger>
            <TabsTrigger value="manuals">Manuels</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="quotes">Devis</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
            <TabsTrigger value="seo">SEO</TabsTrigger>
            <TabsTrigger value="smtp">SMTP</TabsTrigger>
          </TabsList>

          {/* Courses Tab */}
          <TabsContent value="courses">
            <div className="bg-white border border-[#1F2A4A]/10 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#1F2A4A]/10">
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Formation</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Type</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Prix</th>
                      <th className="text-left p-4 text-sm font-medium text-muted-foreground">Statut</th>
                      <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((course) => (
                      <tr key={course.id} className="border-b border-[#1F2A4A]/10 last:border-0">
                        <td className="p-4">
                          <div>
                            <div className="font-medium">{course.title}</div>
                            <div className="text-sm text-muted-foreground">{categoryLabels[course.category]}</div>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant={course.course_type === 'online' ? 'default' : 'secondary'}>
                            {course.course_type === 'online' ? 'En ligne' : 'Sur place'}
                          </Badge>
                        </td>
                        <td className="p-4">{course.price.toFixed(2)} €</td>
                        <td className="p-4">
                          <Badge variant={course.is_published ? 'default' : 'outline'} className={course.is_published ? 'bg-green-500/20 text-green-500' : ''}>
                            {course.is_published ? 'Publié' : 'Brouillon'}
                          </Badge>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-2">
                            {!course.is_published && (
                              <Button size="sm" variant="ghost" onClick={() => handlePublish(course.id)}>
                                Publier
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => editCourse(course)} data-testid={`course-edit-${course.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(course.id)} data-testid={`course-delete-${course.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UserManagementAdmin />
          </TabsContent>

          {/* Trainers Tab */}
          <TabsContent value="trainers">
            <TrainersAdmin />
          </TabsContent>

          {/* Dossier pipeline */}
          <TabsContent value="dossiers">
            <DossierPipelineAdmin />
          </TabsContent>

          {/* Insights Tab */}
          <TabsContent value="insights">
            {!insights || insights.total_applications === 0 ? (
              <div className="text-center py-16 bg-white border border-[#1F2A4A]/10 rounded-2xl">
                <BarChart3 className="w-14 h-14 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Pas encore de candidatures</h3>
                <p className="text-muted-foreground">
                  Les analyses apparaîtront dès que les candidats commenceront à remplir le parcours.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-beige/10 via-beige/5 to-transparent border border-beige/30 rounded-2xl p-6">
                  <p className="text-xs font-semibold text-[#C9B896] uppercase tracking-[0.25em] mb-2">
                    Insights candidatures
                  </p>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl font-heading font-bold text-foreground" data-testid="insights-total">
                      {insights.total_applications}
                    </span>
                    <span className="text-muted-foreground">candidatures qualifiées analysées</span>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <InsightCard
                    title="Sources de découverte"
                    subtitle="Comment les candidats vous trouvent"
                    items={insights.discovery_sources}
                    labelMap={insightLabels.discovery}
                  />
                  <InsightCard
                    title="Réseaux sociaux détaillés"
                    subtitle="Plateforme utilisée (parmi ceux venus via réseaux sociaux)"
                    items={insights.discovery_details}
                    labelMap={insightLabels.social}
                  />
                  <InsightCard
                    title="Situation actuelle"
                    subtitle="Profil professionnel des candidats"
                    items={insights.current_situations}
                    labelMap={insightLabels.situation}
                  />
                  <InsightCard
                    title="Ambitions"
                    subtitle="Place visée pour la sellerie dans leur parcours"
                    items={insights.professional_goals}
                    labelMap={insightLabels.goal}
                  />
                </div>
              </div>
            )}
          </TabsContent>

          {/* FAQ Tab */}
          <TabsContent value="faq">
            <FAQAdmin />
          </TabsContent>

          {/* Knowledge Base Tab */}
          <TabsContent value="kb">
            <KBAdmin />
          </TabsContent>

          {/* Manuels & QCM Tab */}
          <TabsContent value="manuals">
            <ManualsAdmin />
          </TabsContent>

          {/* Leads Marketing Tab */}
          <TabsContent value="leads">
            <LeadsMarketing />
          </TabsContent>

          {/* Devis sellerie — public quote requests */}
          <TabsContent value="quotes">
            <QuoteRequestsAdmin />
          </TabsContent>

          <TabsContent value="messages">
            <MessagesInboxAdmin />
          </TabsContent>

          <TabsContent value="emails">
            <EmailHistoryAdmin />
          </TabsContent>

          <TabsContent value="announcement">
            <AnnouncementAdmin />
          </TabsContent>

          {/* SEO & Blog Tracking Tab */}
          <TabsContent value="seo">
            <SeoTrackingAdmin />
          </TabsContent>

          {/* SMTP Settings Tab */}
          <TabsContent value="smtp">
            <SmtpSettingsAdmin />
          </TabsContent>
        </Tabs>
      </div>

      {/* Global course create/edit dialog */}
      <Dialog open={showCourseModal} onOpenChange={(open) => { setShowCourseModal(open); if (!open) resetForm(); }}>
        <DialogContent className="bg-white border border-[#1F2A4A]/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedCourse ? 'Modifier la formation' : 'Créer une formation'}
            </DialogTitle>
          </DialogHeader>
          {CourseFormFields}
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
};


// ─────────────────────────────────────────────
// FAQ Admin
// ─────────────────────────────────────────────
const FAQ_CATEGORIES = [
  { key: 'general', label: 'Général' },
  { key: 'formations', label: 'Formations' },
  { key: 'financement', label: 'Financement' },
  { key: 'inscription', label: 'Inscription' },
  { key: 'pratique', label: 'Pratique' },
  { key: 'apres', label: 'Après la formation' },
];

const FAQ_CAT_LABELS = FAQ_CATEGORIES.reduce((m, c) => ({ ...m, [c.key]: c.label }), {});

const FAQAdmin = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const empty = { question: '', answer: '', category: 'general', order: 0, is_published: true };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.listFaq({ all_items: true });
      setItems(data);
    } catch (e) {
      toast.error("Erreur lors du chargement de la FAQ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm(item); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.question || !form.answer) {
      toast.error("Question et réponse obligatoires");
      return;
    }
    try {
      if (editing) {
        await api.updateFaq(editing.id, form);
        toast.success("Question mise à jour");
      } else {
        await api.createFaq(form);
        toast.success("Question créée");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Supprimer "${item.question}" ?`)) return;
    try {
      await api.deleteFaq(item.id);
      toast.success("Question supprimée");
      load();
    } catch (e) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const togglePublished = async (item) => {
    try {
      await api.updateFaq(item.id, { is_published: !item.is_published });
      load();
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const grouped = items.reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <div className="space-y-8" data-testid="faq-admin">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl text-foreground">Gestion de la FAQ</h2>
          <p className="text-muted-foreground text-sm mt-1">{items.length} question{items.length > 1 ? 's' : ''} · {items.filter(i => i.is_published).length} publiée{items.filter(i => i.is_published).length > 1 ? 's' : ''}</p>
        </div>
        <Button onClick={openCreate} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full" data-testid="faq-create-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvelle question
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-20 bg-white border border-[#1F2A4A]/8 rounded-xl animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-[#1F2A4A]/8">
          <HelpCircle className="w-16 h-16 text-[#C9B896] mx-auto mb-4 opacity-40" />
          <h3 className="text-xl font-semibold mb-2 text-foreground">Aucune question pour le moment</h3>
          <p className="text-muted-foreground mb-5">Crée ta première question pour démarrer la FAQ publique.</p>
          <Button onClick={openCreate} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full">
            <Plus className="w-4 h-4 mr-2" /> Première question
          </Button>
        </div>
      ) : (
        <div className="space-y-10">
          {FAQ_CATEGORIES.filter(c => grouped[c.key]).map(({ key, label }) => (
            <div key={key}>
              <div className="flex items-baseline gap-4 mb-4">
                <span className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896]">{label}</span>
                <span className="h-px flex-1 bg-[#1F2A4A]/10" />
                <span className="text-xs text-muted-foreground">{grouped[key].length}</span>
              </div>
              <ul className="space-y-2">
                {grouped[key].sort((a, b) => a.order - b.order).map((item) => (
                  <li key={item.id} className="bg-white border border-[#1F2A4A]/8 hover:border-[#C9B896]/50 rounded-xl p-5 transition-colors" data-testid={`faq-row-${item.id}`}>
                    <div className="flex items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[10px] font-mono text-[#C9B896]">#{item.order || 0}</span>
                          {!item.is_published && (
                            <Badge className="bg-amber-500/15 text-amber-700 border-0 text-[9px] uppercase tracking-wider">Brouillon</Badge>
                          )}
                        </div>
                        <h4 className="text-base font-medium text-foreground mb-1.5">{item.question}</h4>
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.answer}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => togglePublished(item)} title={item.is_published ? "Dépublier" : "Publier"} className="w-8 h-8 rounded-full hover:bg-[#1F2A4A]/5 flex items-center justify-center transition-colors" data-testid={`faq-toggle-${item.id}`}>
                          {item.is_published ? <Check className="w-4 h-4 text-green-600" /> : <Eye className="w-4 h-4 text-[#4A5568]" />}
                        </button>
                        <button onClick={() => openEdit(item)} title="Modifier" className="w-8 h-8 rounded-full hover:bg-[#1F2A4A]/5 flex items-center justify-center transition-colors" data-testid={`faq-edit-${item.id}`}>
                          <Edit className="w-4 h-4 text-[#4A5568]" />
                        </button>
                        <button onClick={() => handleDelete(item)} title="Supprimer" className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center transition-colors" data-testid={`faq-delete-${item.id}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader>
            <DialogTitle>{editing ? 'Modifier la question' : 'Nouvelle question'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Modifie le contenu, la catégorie ou le statut de publication de cette question.'
                : 'Renseigne une question / réponse, sa catégorie et son ordre d\'affichage dans la FAQ publique.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Question</Label>
              <Input
                value={form.question}
                onChange={(e) => setForm({ ...form, question: e.target.value })}
                placeholder="Ex : Puis-je financer ma formation via France Travail, FAFCEA ou OPCO ?"
                className="bg-transparent border-[#1F2A4A]/15"
                data-testid="faq-form-question"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Réponse</Label>
              <Textarea
                value={form.answer}
                onChange={(e) => setForm({ ...form, answer: e.target.value })}
                placeholder="Réponse détaillée. Tu peux faire plusieurs paragraphes en sautant des lignes."
                className="bg-transparent border-[#1F2A4A]/15 min-h-[150px]"
                data-testid="faq-form-answer"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-transparent border-[#1F2A4A]/15" data-testid="faq-form-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FAQ_CATEGORIES.map(c => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Ordre</Label>
                <Input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                  className="bg-transparent border-[#1F2A4A]/15"
                  data-testid="faq-form-order"
                />
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_published}
                onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
                className="w-4 h-4 accent-[#C9B896]"
                data-testid="faq-form-published"
              />
              <span className="text-sm text-foreground">Publier immédiatement sur le site</span>
            </label>
            <div className="flex justify-end gap-3 pt-3">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full" data-testid="faq-form-submit">
                {editing ? 'Mettre à jour' : 'Créer la question'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
